import type { MutableRefObject, Ref, RefCallback, RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { ImageProps } from 'components/common/Image';

// Minimum horizontal travel (px) between touchstart and touchend to count as a swipe rather than a tap.
export const SWIPE_THRESHOLD_PX = 50;
// Total travel (px) before the gesture axis (swipe vs. scroll) locks.
const AXIS_LOCK_THRESHOLD_PX = 4;

export type SwipeDirection = 'LEFT' | 'RIGHT';

interface UseCardSwipeOptions {
  total: number;
  allImages: ImageProps[];
  enabled: boolean;
  /**
   * Fired once per committed swipe (past SWIPE_THRESHOLD_PX).
   * `index` is the image the user swiped AWAY from, not the destination.
   */
  onSwipe?: (info: { direction: SwipeDirection; index: number }) => void;
  /**
   * Marks where the details block starts. A swipe only arms if the touch begins above this
   * (over the image), so swiping the name/price scrolls/taps normally. Omit to arm anywhere.
   */
  boundaryRef?: RefObject<HTMLElement | null>;
  /**
   * An external ref (e.g. the IntersectionObserver ref from useInView) that also needs the same node.
   * The returned `cardRef` fans the element out to both this and the hook's internal listener host,
   * so the caller attaches a single ref to the card. Accepts a callback ref, an object ref, or undefined.
   */
  forwardedRef?: Ref<HTMLElement | null>;
}

interface CardSwipe {
  activeIndex: number;
  /** Callback ref to attach to the card <article> (the swipe listener host, merged with `forwardedRef`). */
  cardRef: RefCallback<HTMLElement | null>;
  didSwipeRef: { current: boolean };
}

/**
 * Swipe controller for the mobile multi-image preview on a PLP product card.
 *
 * Binds native touch listeners to the card; `touchmove` is non-passive so a horizontal swipe can
 * `preventDefault()` the page scroll (React's synthetic `onTouchMove` is passive and cannot). The axis
 * locks within the first few px of travel, making swipe and scroll mutually exclusive: a horizontal
 * lock cancels the scroll and cycles the image; a vertical lock is left to native scroll.
 *
 * - A horizontal move past `SWIPE_THRESHOLD_PX` advances/retreats the angle index (wrapping) and sets
 *   `didSwipeRef` so the trailing tap-to-navigate is suppressed.
 * - Arms only when the touch starts above `boundaryRef` (over the image, not the details).
 * - Resets to MAIN on image-set change (swatch swap); inert unless `enabled`.
 */
export const useCardSwipe = ({ total, allImages, enabled, onSwipe, boundaryRef, forwardedRef }: UseCardSwipeOptions): CardSwipe => {
  const [activeIndex, setActiveIndex] = useState(0);
  const elementRef = useRef<HTMLElement | null>(null);
  const didSwipeRef = useRef(false);

  // Merged callback ref: store the node for our listeners and forward it to any external ref
  // (callback, object, or none). Lets the caller attach a single `ref` to the card.
  const cardRef = useCallback<RefCallback<HTMLElement | null>>(
    node => {
      elementRef.current = node;
      if (typeof forwardedRef === 'function') {
        forwardedRef(node);
      } else if (forwardedRef) {
        (forwardedRef as MutableRefObject<HTMLElement | null>).current = node;
      }
    },
    [forwardedRef]
  );

  // Snap back to MAIN whenever the image set changes (e.g. a swatch swap).
  useEffect(() => {
    setActiveIndex(0);
  }, [allImages]);

  // Bind touch listeners natively (not via React props) so `touchmove` can be non-passive and cancel
  // the scroll. The handlers read props/state from this closure, so the effect re-subscribes when those
  // change; re-binding on a single node is cheap and only happens between gestures, never mid-swipe.
  useEffect(() => {
    const element = elementRef.current;
    if (!enabled || !element) {
      return;
    }

    let touchStartX = 0;
    let touchStartY = 0;
    let isTracking = false;
    let isHorizontalSwipe = false;
    let isAxisLocked = false;

    // In the image area unless the touch begins at/below the details block. No boundary or no Y → in-area.
    const isInImageArea = (clientY?: number): boolean => {
      const boundaryTop = boundaryRef?.current?.getBoundingClientRect().top;
      if (clientY === undefined || boundaryTop === undefined) {
        return true;
      }
      return clientY < boundaryTop;
    };

    const handleTouchStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      // Reset on every touchstart so a swipe-with-no-click can't swallow the next tap.
      didSwipeRef.current = false;
      if (!touch || !isInImageArea(touch.clientY)) {
        isTracking = false; // touch on the details text → don't arm; falls through to a tap/scroll.
        return;
      }
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      isTracking = true;
      isHorizontalSwipe = false;
      isAxisLocked = false;
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (!isTracking) {
        return;
      }
      const touch = event.touches[0];
      if (!touch) {
        return;
      }
      const deltaX = touch.clientX - touchStartX;
      const deltaY = touch.clientY - touchStartY;
      // Lock the axis as soon as total travel is meaningful — before the browser commits to a scroll —
      // classifying by the dominant axis at that moment (Swiper/Embla use the same first-move lock).
      if (!isAxisLocked && Math.hypot(deltaX, deltaY) > AXIS_LOCK_THRESHOLD_PX) {
        isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);
        isAxisLocked = true;
      }
      // A horizontal swipe owns the gesture and cancels the scroll; a vertical (or not-yet-locked)
      // gesture is left entirely to the browser so native scrolling stays seamless. Guard on
      // `cancelable` so a call after the browser already began scrolling is a silent no-op.
      if (isHorizontalSwipe && event.cancelable) {
        event.preventDefault();
      }
    };

    const handleTouchEnd = (event: TouchEvent) => {
      if (!isTracking) {
        return;
      }
      isTracking = false;
      // Swipe and scroll are mutually exclusive: only a gesture that locked to the horizontal axis can
      // commit an image change. A vertical (scroll) gesture never cycles, so the two never overlap.
      if (!isHorizontalSwipe || total <= 1) {
        return;
      }
      const touch = event.changedTouches[0];
      if (!touch) {
        return;
      }
      const deltaX = touch.clientX - touchStartX;
      if (Math.abs(deltaX) < SWIPE_THRESHOLD_PX) {
        return; // locked horizontal but too short to commit → treat as a tap (navigation proceeds).
      }
      didSwipeRef.current = true; // a real swipe → suppress the click

      const direction: SwipeDirection = deltaX < 0 ? 'LEFT' : 'RIGHT';
      // activeIndex here is the image currently on screen — the one being swiped AWAY from.
      onSwipe?.({ direction, index: activeIndex });

      // Left swipe (deltaX < 0) → next angle; right swipe (deltaX > 0) → previous. Wrap.
      setActiveIndex(i => (deltaX < 0 ? (i + 1) % total : (i - 1 + total) % total));
    };

    const handleTouchCancel = () => {
      isTracking = false;
      isHorizontalSwipe = false;
      isAxisLocked = false;
    };

    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: false });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });
    element.addEventListener('touchcancel', handleTouchCancel, { passive: true });

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
      element.removeEventListener('touchcancel', handleTouchCancel);
    };
  }, [enabled, total, onSwipe, boundaryRef, activeIndex]);

  return { activeIndex, cardRef, didSwipeRef };
};
